import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { isJwtExpired } from '@/lib/auth/jwt';
import { clearAllAuthStorage, refreshStorage, tokenStorage } from '@/lib/auth/storage';
import { recordNetworkFailure } from '@/lib/issue-reporting/debugBuffer';

// Empty default = same-origin requests. In dev that means Vite's /api proxy
// forwards to http://localhost:8080 (see vite.config.ts). In production, set
// VITE_API_BASE_URL to the absolute backend URL at build time.
const DEFAULT_BASE_URL = '';
const REQUEST_TIMEOUT_MS = 30_000;

/** Module-level single-flight guard — prevents redirect loops on cascading 401s. */
let isRedirecting = false;

/**
 * Module-level single-flight refresh promise (Plan 06-03 AUTH-103).
 *
 * When ANY 401 with `Token-Expired: true` arrives, we want EXACTLY ONE network call
 * to `POST /api/auth/refresh` to be in flight at a time. Any concurrent 401 awaits
 * the same `refreshPromise` (resolves to the new access token) and then replays its
 * own original request. On reject, we null the promise and fall through to the
 * existing clear+redirect path.
 */
let refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;

/** Marker on a config that has already been replayed once — prevents infinite refresh loops. */
type RetryableConfig = InternalAxiosRequestConfig & { _replayed?: boolean; _startTime?: number };

/**
 * Feed the issue-reporting diagnostic buffer with a FAILED request. Excludes the auth refresh
 * dance and the issue-report submit itself (don't capture the user's own in-flight report).
 */
function captureNetworkFailure(error: AxiosError): void {
  const cfg = error.config as RetryableConfig | undefined;
  const url = cfg?.url ?? error.config?.url ?? '';
  if (url.includes('/auth/') || url.includes('/api/issue-reports')) return;
  const start = cfg?._startTime;
  recordNetworkFailure({
    method: cfg?.method ?? 'get',
    url,
    status: error.response?.status ?? 0,
    ...(error.response?.statusText !== undefined && error.response.statusText !== ''
      ? { statusText: error.response.statusText }
      : {}),
    ...(typeof start === 'number' ? { durationMs: Date.now() - start } : {}),
  });
}

function redirectToLogin(reason: string): void {
  if (isRedirecting) return;
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path === '/login') return; // Don't bounce while already there.
  isRedirecting = true;
  // Surface a one-shot reason for the LoginForm to display.
  try {
    window.sessionStorage.setItem('aryan:auth-message', reason);
  } catch {
    // Ignore storage errors.
  }
  // Notify the AuthContext to drop in-memory state.
  window.dispatchEvent(new Event('auth:logout'));
  window.setTimeout(() => {
    window.location.href = '/login';
    // Reset after the navigation has had a chance to start.
    window.setTimeout(() => {
      isRedirecting = false;
    }, 1000);
  }, 50);
}

const baseURL = (import.meta.env.VITE_API_BASE_URL ?? DEFAULT_BASE_URL).replace(/\/$/, '');

/** Singleton axios client. Import from `@/lib/api/axios` everywhere. */
export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Lazy import of `services/auth` to avoid the circular dep:
 *   `services/auth` imports `api` from this very module.
 * The dynamic import resolves at first call only, then caches.
 */
async function performRefresh(): Promise<{ accessToken: string; refreshToken: string }> {
  const refresh = refreshStorage.read();
  if (refresh === null) throw new Error('NoRefreshToken');
  const mod = await import('@/services/auth');
  const r = await mod.refreshRequest(refresh);
  tokenStorage.write(r.accessToken);
  refreshStorage.write(r.refreshToken);
  return { accessToken: r.accessToken, refreshToken: r.refreshToken };
}

// Request interceptor: attach JWT bearer; on local expiry, silently refresh
// when a refresh token exists ("Remember me for 30 days" path) — only fall
// through to clear+redirect when no recovery is possible.
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Stamp request start for the diagnostic buffer's durationMs (issue reporting).
    (config as RetryableConfig)._startTime = Date.now();
    const url = config.url ?? '';
    const isAuthEndpoint = url.includes('/auth/');
    const token = tokenStorage.read();
    if (token === null) return config;

    if (isAuthEndpoint) {
      // Auth endpoints (login, refresh, logout) are exempt from client-side
      // expiry checks — refresh in particular must be allowed to carry the
      // (now-expired) access token while it presents the refresh token in the
      // body.
      config.headers.set('Authorization', `Bearer ${token}`);
      return config;
    }

    if (!isJwtExpired(token)) {
      config.headers.set('Authorization', `Bearer ${token}`);
      return config;
    }

    // Expired locally — try a silent refresh before giving up. Shares the
    // module-level single-flight refreshPromise with the response interceptor
    // so concurrent requests still only fire ONE call to /api/auth/refresh.
    if (refreshStorage.read() !== null) {
      try {
        refreshPromise ??= performRefresh();
        const { accessToken } = await refreshPromise;
        refreshPromise = null;
        config.headers.set('Authorization', `Bearer ${accessToken}`);
        return config;
      } catch {
        refreshPromise = null;
        // Refresh itself failed — fall through to clear+redirect below.
      }
    }

    clearAllAuthStorage();
    redirectToLogin('Your session has expired. Please log in again.');
    throw new AxiosError('Local JWT expired', 'ERR_LOCAL_JWT_EXPIRED', config);
  },
  (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

// Response interceptor: handle 401s with single-flight refresh, then clear+redirect on failure.
api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (error instanceof AxiosError) {
      // Buffer the failure for the "Report an issue" diagnostics (scrubbed inside).
      captureNetworkFailure(error);
      const status = error.response?.status;
      const url = error.config?.url ?? '';
      const isAuthEndpoint = url.includes('/auth/');
      const tokenExpiredHeaderRaw: unknown = error.response?.headers['token-expired'];
      const tokenExpiredHeader =
        typeof tokenExpiredHeaderRaw === 'string' && tokenExpiredHeaderRaw.toLowerCase() === 'true';

      if (status === 401 && !isAuthEndpoint) {
        const config = error.config as RetryableConfig | undefined;
        const hasRefreshToken = refreshStorage.read() !== null;

        // Single-flight refresh path — only when the backend signals the access token
        // expired AND we have a refresh token AND this exact request hasn't already
        // been replayed once.
        if (
          tokenExpiredHeader &&
          hasRefreshToken &&
          config !== undefined &&
          config._replayed !== true
        ) {
          try {
            refreshPromise ??= performRefresh();
            const { accessToken } = await refreshPromise;
            refreshPromise = null;
            config._replayed = true;
            config.headers.set('Authorization', `Bearer ${accessToken}`);
            return await api.request(config);
          } catch {
            // Refresh failed — fall through to clear+redirect.
            refreshPromise = null;
          }
        }

        const reason = tokenExpiredHeader
          ? 'Your session has expired. Please log in again.'
          : 'Authentication required. Please log in.';
        clearAllAuthStorage();
        redirectToLogin(reason);
      }
    }
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

/** Reset the redirect guard. EXPORTED FOR TESTS only. */
export function _resetRedirectGuardForTests(): void {
  isRedirecting = false;
}

/** Reset the single-flight refresh promise. EXPORTED FOR TESTS only. */
export function _resetRefreshPromiseForTests(): void {
  refreshPromise = null;
}
